/**
 * Where a key press moves FOCUS inside a tab strip, and the two ids a strip and its panels agree on.
 *
 * Promoted out of `pages/client-detail/model/detailTabs.ts` on 17 September 2026, when `/raporty`
 * became the second consumer of the strip. The rule it records is the client detail's and has not
 * changed:
 *
 * ── Focus, not selection ─────────────────────────────────────────────────────────────────────
 * A strip that SELECTS on every arrow is what WAI-ARIA calls automatic activation, and it allows it
 * "when the panels are cheap to render". Neither of this app's two strips is: each tab owns its own
 * queries and mounts only while visible, so arrowing from the first tab to the fifth used to fire
 * four tabs' worth of requests on the way past and push four history entries with them. Manual
 * activation — arrows move, Enter or Space chooses — is the pattern APG names for exactly that case,
 * and a `<button>` already answers Enter and Space with a click, so choosing needs no key handling.
 *
 * Home and End are part of the same pattern: on a six-tab strip they are the difference between one
 * key and five.
 *
 * No React and no DOM. The order arrives as a parameter, which is what lets a second strip with
 * three tabs reuse the rule rather than restate it.
 */

/**
 * The next tab focus should sit on, or `null` for a key the strip does not own — Enter, Space and
 * Tab especially, because claiming those is what would make the strip impossible to choose from.
 *
 * @param order the strip's tabs, in the order they are drawn. `current` outside it answers `null`.
 */
export function tabFocusTarget<Key extends string>(
  key: string,
  current: Key,
  order: readonly Key[],
): Key | null {
  const index = order.indexOf(current);
  if (index === -1) return null;

  if (key === 'Home') return order[0] ?? null;
  if (key === 'End') return order[order.length - 1] ?? null;

  const step = key === 'ArrowRight' ? 1 : key === 'ArrowLeft' ? -1 : 0;
  if (step === 0) return null;
  // Wrapping, which is what the tabs pattern specifies and what a trainer pressing Left on the
  // first tab expects.
  return order[(index + step + order.length) % order.length] ?? null;
}

/**
 * The DOM id of a tab's button, and of the panel it controls.
 *
 * Two functions rather than a convention written out at four call sites: the strip stamps the
 * button's id, the panel stamps its own, each points at the other through `aria-controls` and
 * `aria-labelledby`, and a screen that has to move focus to a tab from OUTSIDE the strip — the
 * client detail's summary column hands it off when the column leaves the screen — looks the button
 * up by the same spelling. Four places to disagree, collapsed into one.
 *
 * They assume ONE strip per page, which is what both consumers are. A second strip on one screen
 * would need a prefix, and that is a change to make when it exists rather than a parameter nobody
 * passes.
 */
export function tabButtonId(tab: string): string {
  return `tab-${tab}`;
}

export function tabPanelId(tab: string): string {
  return `panel-${tab}`;
}
